/**
 * Identifiers and masking (FTD §3.3, AC §4.5–§4.6, §14).
 *
 * - Masks use `•` (U+2022), never `*` or `0`, which can be read as digits.
 * - Identifiers (references, Sheba, mobile, national code) are left-to-right inside RTL text. On the
 *   web they go in `<bdi dir="ltr">` (the `Ltr` component in `@saay/ui` uses {@link LTR_ISOLATE_PROPS});
 *   in plain-text contexts (React Native `Text`, `aria-label`, titles) use {@link isolateLtr}.
 * - Masked identifiers a person reads (mobile, national code, card) show Persian digits, still left to
 *   right (owner W4 decision). Unmasked references, Sheba, and request IDs keep ASCII digits so they
 *   can be read back and compared exactly.
 */

import { normalizeDigits, toPersianDigits } from './digits.js';

export const MASK_CHAR = '•';

/** Props for a web `<bdi>` element that isolates a left-to-right identifier. */
export const LTR_ISOLATE_PROPS = { dir: 'ltr' } as const;

/** Wraps text in Unicode LEFT-TO-RIGHT ISOLATE … POP DIRECTIONAL ISOLATE for plain-text contexts. */
export function isolateLtr(text: string): string {
  return `⁦${text}⁩`;
}

function mask(count: number): string {
  return MASK_CHAR.repeat(Math.max(0, count));
}

/** Converts an E.164 Iranian mobile (`+989121234567`) to the local form `09121234567`. */
export function toLocalMobile(mobile: string): string {
  return mobile.startsWith('+98') ? `0${mobile.slice(3)}` : mobile;
}

/** `+989121234567` → `۰۹۱۲•••۴۵۶۷`. */
export function maskMobile(mobileE164: string): string {
  const local = toLocalMobile(mobileE164);

  if (!/^09\d{9}$/.test(local)) {
    return mask(local.length);
  }

  return toPersianDigits(`${local.slice(0, 4)}${mask(3)}${local.slice(-4)}`);
}

/** The mask character the API uses in `*Masked` fields (AC §4.5, §4.6, §14). */
const SERVER_MASK = /\*/g;

/** What a server-masked value is, so it is shown in its local form (FTD §3.3). */
export type MaskedKind = 'card' | 'mobile' | 'nationalCode' | 'sheba' | 'text';

/** Masked kinds shown with Persian digits (owner W4 decision: mobile, card, national code). */
const PERSIAN_DIGIT_KINDS: ReadonlySet<MaskedKind> = new Set(['card', 'mobile', 'nationalCode']);

/**
 * The one display rule for every server-masked value (FTD §3.3): the API masks with `*`, the UI shows
 * `•`, which cannot be read as a digit. A mobile is also shown in its local form (`+98912***4567` →
 * `۰۹۱۲•••۴۵۶۷`). A mobile, national code, or card shows Persian digits, still left to right. The value
 * is never unmasked or re-masked here; the server decides what is hidden. On the web, render the result
 * in `<bdi dir="ltr">` (the `Masked` component in `@saay/ui`).
 */
export function displayServerMask(masked: string, kind: MaskedKind = 'text'): string {
  const local = kind === 'mobile' ? toLocalMobile(masked) : masked;
  const shown = local.replace(SERVER_MASK, MASK_CHAR);
  return PERSIAN_DIGIT_KINDS.has(kind) ? toPersianDigits(shown) : shown;
}

/**
 * Shows a server-masked mobile (`mobileMasked`, `+98912***4567`, AC §4.5) in the local form with the
 * display mask character and Persian digits: `۰۹۱۲•••۴۵۶۷`.
 */
export function displayMaskedMobile(mobileMasked: string): string {
  return displayServerMask(mobileMasked, 'mobile');
}

/** `0012345678` → `••••••۵۶۷۸`. */
export function maskNationalCode(nationalCode: string): string {
  return toPersianDigits(`${mask(nationalCode.length - 4)}${nationalCode.slice(-4)}`);
}

/** A server-masked national code (`******7890`, AC §9.1) with the display mask: `••••••۷۸۹۰`. */
export function displayMaskedNationalCode(masked: string): string {
  return displayServerMask(masked, 'nationalCode');
}

/**
 * Shows any server-masked text (for example a buyer `displayNameMasked` such as `م*** ا***`) with the
 * display mask character: `م••• ا•••`.
 */
export function displayMaskedText(masked: string): string {
  return displayServerMask(masked, 'text');
}

/** An Iranian mobile in E.164 (AC §4.5): `+989XXXXXXXXX`. */
export const MOBILE_E164_PATTERN = /^\+989\d{9}$/;

/**
 * Normalizes a typed Iranian mobile number (Persian or Latin digits; `09…`, `9…`, `989…`, `0098…`,
 * or `+989…`; spaces and dashes allowed) to E.164 `+989XXXXXXXXX`, or `null` when it is not one.
 */
export function normalizeMobileInput(input: string): string | null {
  const compact = normalizeDigits(input).replace(/[\s\-()‌]/g, '');
  const match = /^(?:\+98|0098|98|0)?(9\d{9})$/.exec(compact);
  return match === null ? null : `+98${match[1] ?? ''}`;
}

/** A server-masked Sheba (`IR** **** … **12 34`) with the display mask character (FTD §3.3). */
export function displayMaskedSheba(masked: string): string {
  return displayServerMask(masked, 'sheba');
}

/** `IR820540102680020817909002` → `IR82••••••••••••••••••9002`. */
export function maskSheba(sheba: string): string {
  if (sheba.length <= 8) {
    return mask(sheba.length);
  }

  return `${sheba.slice(0, 4)}${mask(sheba.length - 8)}${sheba.slice(-4)}`;
}
